// Canonical, dependency-free v2 contract. Publish this file with contract.ts.
// Browser attribution is descriptive metadata and never authorizes an operation.
export const ANALYTICS_V2_VERSION = 2 as const;
export const LEGACY_ANALYTICS_VERSION = 1 as const;
export const ANALYTICS_CONTEXT_HEADER = 'X-Analytics-Context';
export const HEADER_MAX_ENCODED_BYTES = 4096;
export const ANALYTICS_MAX_PROPERTY_BYTES = 16000;
export const ANALYTICS_MAX_ARRAY_ITEMS = 100;

export const V2_FRONTEND_EVENTS = [
  'page_viewed', 'page_engagement', 'search_submitted', 'filter_changed',
  'suggestion_selected', 'ui_interaction', 'detail_opened', 'download_created',
  'network_failed', 'agent_prompt_submitted', 'application_error',
  'workflow_started', 'company_selection_mode_changed', 'company_selection_changed',
  'related_people_requested', 'map_view_requested', 'company_view_requested',
  'company_tab_viewed', 'portfolio_match_inspected', 'filters_applied',
  'filter_apply_blocked', 'pagination_changed', 'results_page_viewed',
  'people_selection_changed', 'contact_unlock_requested', 'contact_unlock_blocked',
  'contact_reveal_displayed', 'people_list_add_requested', 'people_list_add_blocked',
  'map_location_opened', 'map_cluster_expanded', 'detail_content_ready',
  'detail_content_failed',
] as const;
export const V2_BACKEND_EVENTS = [
  'signed_in', 'registered', 'signed_out', 'search_completed', 'search_page_returned',
  'search_count_resolved', 'search_failed', 'operation_completed', 'operation_failed',
  'credits_consumed', 'unlock_completed', 'unlock_failed', 'agent_completed',
  'agent_failed', 'agent_cancelled', 'action_blocked',
] as const;
export type FrontendEventV2 = (typeof V2_FRONTEND_EVENTS)[number];
export type BackendEventV2 = (typeof V2_BACKEND_EVENTS)[number];
export type AnalyticsEventV2 = FrontendEventV2 | BackendEventV2;
export type AnalyticsSource = 'frontend' | 'backend';
export type ExecutionKind = 'interactive' | 'automatic' | 'deferred';
export type SearchPhaseV2 = 'submit' | 'page' | 'count';
export const WORKFLOW_TYPES = [
  'account_selection', 'company_research', 'people_prospecting',
  'product_discovery', 'map_exploration',
] as const;
export type WorkflowType = (typeof WORKFLOW_TYPES)[number];
export const SEARCH_TRIGGERS = [
  'search_button', 'apply_filters', 'initial_load', 'inherited_selection',
  'deep_link', 'user_pagination', 'page_size_change', 'automatic_batch', 'retry',
] as const;
export type SearchTrigger = (typeof SEARCH_TRIGGERS)[number];
export const ANALYTICS_SURFACES = [
  'home', 'accounts_results', 'account_company_drawer', 'company_detail',
  'company_products_tab', 'company_people_tab', 'company_locations_tab',
  'people_results', 'products_results', 'packaging_results', 'map_results',
  'product_detail_drawer', 'related_people', 'lists', 'profile', 'support',
  'agent', 'login', 'unknown',
] as const;
export type AnalyticsSurface = (typeof ANALYTICS_SURFACES)[number];
export type CompanyType = 'marketer' | 'manufacturer';
export type CountStatus = 'pending' | 'ready' | 'unavailable';
export type TenantResolution = 'resolved' | 'none' | 'unavailable';
export const ENTITY_NAMESPACES = [
  'internal_marketer', 'internal_manufacturer', 'forager_organization',
  'forager_person', 'internal_person', 'internal_product', 'internal_ingredient',
  'internal_application', 'internal_category', 'internal_subcategory',
  'internal_packaging', 'client_category', 'client_group', 'client_product',
  'product_list', 'people_list', 'product_list_item', 'location',
  'provider_industry', 'provider_location', 'provider_skill', 'coverage',
] as const;
export type EntityNamespace = (typeof ENTITY_NAMESPACES)[number];
export type LabelSource = 'loaded_entity' | 'dictionary' | 'browser_option';
export type LabelResolutionStatus = 'resolved' | 'partial' | 'unresolved';

export interface EntityIdentity {
  readonly id: string;
  readonly namespace: EntityNamespace;
}
export interface EntityReference extends EntityIdentity {
  readonly linked_ids?: readonly EntityIdentity[];
  readonly name?: string;
  readonly label_source?: LabelSource;
  readonly label_resolution_status: 'resolved' | 'unresolved';
  readonly company_type?: CompanyType;
}
export interface SelectionSnapshot {
  readonly selection_id: string;
  readonly selection_version: number;
  readonly selected_count: number;
  readonly included_count: number;
  readonly truncated: boolean;
  readonly entities: readonly EntityReference[];
}

// No user, tenant, device, session, current search, operation, or phase here:
// those are trusted server fields or use the five existing dedicated headers.
export interface AttributionContext {
  readonly workflow_id?: string;
  readonly workflow_type?: WorkflowType;
  readonly parent_workflow_id?: string;
  readonly selection_id?: string;
  readonly selection_version?: number;
  readonly action_id?: string;
  readonly attempt_id?: string;
  readonly origin_page_view_id?: string;
  readonly origin_search_id?: string;
  readonly parent_search_id?: string;
  readonly page_view_id?: string;
  readonly surface?: AnalyticsSurface;
  readonly source_surface?: AnalyticsSurface;
  readonly source_company_type?: CompanyType | 'mixed';
  readonly destination?: 'people' | 'map' | 'company';
  readonly parent_entity?: EntityReference;
  readonly trigger?: SearchTrigger;
  readonly execution_kind?: ExecutionKind;
  readonly page_number?: number | null;
  readonly page_size?: number;
  readonly previous_page?: number | null;
  readonly offset?: number;
  readonly cursor_present?: boolean;
  readonly selected_count?: number;
  readonly included_count?: number;
  readonly truncated?: boolean;
}
export interface WorkflowContext extends AttributionContext {
  readonly workflow_id: string;
  readonly workflow_type: WorkflowType;
  readonly surface: AnalyticsSurface;
  readonly origin_page_view_id: string;
}
export interface ActionContext extends AttributionContext {
  readonly action_id: string;
  readonly surface: AnalyticsSurface;
}
export type CriteriaProvenance =
  | 'user' | 'portfolio' | 'inherited_company_selection' | 'system_scope';
export type CriterionValue =
  | string | number | boolean | null
  | readonly string[] | readonly number[] | readonly boolean[]
  | { readonly min?: number | string; readonly max?: number | string };
export interface CriteriaDimension {
  readonly key: string;
  readonly value: CriterionValue;
  readonly active: boolean;
  readonly provenance: CriteriaProvenance;
  readonly operator?: 'eq' | 'neq' | 'in' | 'not_in' | 'contains' | 'range' | 'gte' | 'lte';
  readonly unit?: string;
  readonly entities?: readonly EntityReference[];
}
export interface CriteriaSnapshot {
  readonly criteria_version: string;
  readonly query?: string;
  readonly dimensions: readonly CriteriaDimension[];
  readonly filter_keys: readonly string[];
  readonly filter_count: number;
  readonly filter_combination?: string;
  readonly label_resolution_status?: LabelResolutionStatus;
  readonly unresolved_count?: number;
  readonly included_count?: number;
  readonly truncated?: boolean;
}
export interface SearchContext extends AttributionContext {
  readonly search_id: string;
  readonly surface: AnalyticsSurface;
  readonly criteria_version: string;
  readonly trigger: SearchTrigger;
  readonly submitted_criteria?: CriteriaSnapshot;
}
export interface AnalyticsExecutionSnapshot {
  readonly userId?: string;
  readonly clientId?: number | null;
  readonly clientName?: string;
  readonly clientSlug?: string;
  readonly tenantResolution?: TenantResolution;
  readonly applicationScope?: string;
  readonly deviceId?: string;
  readonly sessionId?: number;
  readonly operationId?: string;
  readonly searchId?: string;
  readonly phase?: SearchPhaseV2;
  readonly attribution: AttributionContext;
  readonly schemaVersion: 1 | 2;
  readonly executionKind: ExecutionKind;
  readonly startedAt: number;
  readonly capturedAt: number;
  readonly excluded: boolean;
  readonly accountEpoch?: string;
}
export type AnalyticsRequestContext =
  | {
      readonly executionKind: 'interactive';
      readonly attribution?: AttributionContext;
      readonly action: ActionContext;
      readonly search?: SearchContext;
      readonly phase?: SearchPhaseV2;
      readonly trigger?: SearchTrigger;
      readonly operationId?: string;
      readonly execution?: never;
    }
  | {
      readonly executionKind: 'automatic' | 'deferred';
      readonly execution: AnalyticsExecutionSnapshot;
      readonly attribution?: AttributionContext;
      readonly action?: ActionContext;
      readonly search?: SearchContext;
      readonly phase?: SearchPhaseV2;
      readonly trigger?: SearchTrigger;
      readonly operationId?: string;
    };

export interface PageFacts {
  readonly page_number?: number | null;
  readonly page_size?: number;
  readonly offset?: number;
  readonly cursor_present?: boolean;
}
export interface SearchOutcome extends PageFacts {
  readonly operation: string;
  readonly outcome: 'success' | 'no_results' | 'upstream_unavailable' | 'validation_failed' | 'access_denied' | 'failed';
  readonly returned_count: number | null;
  readonly total_count: number | null;
  readonly count_status: CountStatus;
  readonly zero_results?: boolean;
  readonly submitted_criteria?: CriteriaSnapshot;
  readonly effective_criteria?: CriteriaSnapshot;
  readonly parent_entity?: EntityReference;
  readonly duration_ms?: number;
  readonly error_code?: string;
  readonly provider?: string;
  readonly total_available_count?: number | null;
  readonly portfolio_matched_count?: number | null;
  readonly has_match_criteria?: boolean;
  readonly matched_only?: boolean;
  readonly portfolio_scope?: string;
  readonly location_count?: number | null;
  readonly company_count?: number | null;
  readonly label_resolution_status?: LabelResolutionStatus;
  readonly unresolved_count?: number;
}
export interface CountOutcome {
  readonly operation: string;
  readonly total_count: number | null;
  readonly count_status: CountStatus;
  readonly zero_results?: boolean;
  readonly resolution_id?: string;
  readonly duration_ms?: number;
  readonly error_code?: string;
  readonly total_available_count?: number | null;
  readonly portfolio_matched_count?: number | null;
}
export interface MutationOutcome {
  readonly operation: string;
  readonly outcome: 'success' | 'no_change' | 'partial' | 'failed';
  readonly resource?: EntityReference;
  readonly list_id?: string;
  readonly list_type?: 'people' | 'products';
  readonly requested_count?: number;
  readonly added_count?: number;
  readonly already_present_count?: number;
  readonly removed_count?: number;
  readonly failed_count?: number;
  readonly affected_count?: number;
  readonly created_count?: number;
  readonly updated_count?: number;
  readonly linked_count?: number;
  readonly provider?: string;
  readonly duration_ms?: number;
  readonly error_code?: string;
}
export type ContactType = 'email' | 'phone';
export type UnlockOutcomeCode =
  | 'found' | 'unavailable' | 'provider_failure' | 'invalid_resource'
  | 'retry_restricted' | 'insufficient_credits' | 'access_denied' | 'failed';
export type UnlockOutcome = {
  readonly operation: string;
  readonly contact_type: ContactType;
  readonly attempt_id: string;
  readonly credits_charged: number;
  readonly already_unlocked?: boolean;
  readonly shared_unlock?: boolean;
  readonly purpose?: string;
  readonly reason?: string;
  readonly duration_ms?: number;
} & (
  | { readonly outcome: 'invalid_resource'; readonly resource?: EntityReference }
  | { readonly outcome: Exclude<UnlockOutcomeCode, 'invalid_resource'>; readonly resource: EntityReference }
);
export interface CreditOutcome {
  readonly operation: string;
  readonly amount: number;
  readonly consumption_id: number;
  readonly purpose: string;
  readonly resource: EntityReference;
  readonly unit?: 'credits';
  readonly attempt_id?: string;
  readonly contact_type?: ContactType;
}
export interface AccessOutcome {
  readonly operation: string;
  readonly reason: string;
  readonly feature?: string;
  readonly status_code?: number;
  readonly resource?: EntityReference;
}
export type ReadOutcome = {
  readonly operation: string;
  readonly returned_count?: number | null;
  readonly duration_ms?: number;
  readonly error_code?: string;
} & (
  | { readonly outcome: 'success' | 'no_results'; readonly resource: EntityReference }
  | {
      readonly outcome: 'upstream_unavailable' | 'validation_failed' | 'access_denied' | 'failed';
      readonly resource?: EntityReference;
    }
);
export interface DomainOutcomeByFact {
  search: SearchOutcome;
  count: CountOutcome;
  mutation: MutationOutcome;
  unlock: UnlockOutcome;
  credit: CreditOutcome;
  access: AccessOutcome;
  read: ReadOutcome;
}
export type DomainFact = keyof DomainOutcomeByFact;
export type DomainOutcome = {
  [K in DomainFact]: { readonly fact: K; readonly outcome: DomainOutcomeByFact[K] };
}[DomainFact];

// Runtime emitters fill this envelope. Inputs may omit it; validation never
// permits a caller to override it with a different version or source.
export interface V2CommonProperties extends AttributionContext {
  readonly schema_version?: 2;
  readonly source?: AnalyticsSource;
  readonly environment?: string;
  readonly release_version?: string;
  readonly client_id?: number | null;
  readonly client_name?: string;
  readonly client_slug?: string;
  readonly tenant_resolution?: TenantResolution;
  readonly application_scope?: string;
  readonly search_id?: string;
  readonly operation_id?: string;
  readonly search_phase?: SearchPhaseV2;
  readonly page?: string;
  readonly user_type?: string;
  readonly role?: string;
  readonly is_internal?: boolean;
  readonly properties_truncated?: boolean;
  readonly status_code?: number;
}
export interface SelectionEventProperties extends V2CommonProperties {
  readonly surface: AnalyticsSurface;
  readonly selection: SelectionSnapshot;
  readonly change_type: 'select' | 'deselect' | 'clear' | 'replace';
  readonly selected_after?: boolean;
  readonly resource?: EntityReference;
}
export interface ContactEventProperties extends V2CommonProperties {
  readonly surface: AnalyticsSurface;
  readonly attempt_id: string;
  readonly contact_type: ContactType;
  readonly resource: EntityReference;
  readonly reason?: string;
  readonly availability?: 'new' | 'already_visible' | 'shared' | 'unavailable';
}
export interface ListIntentProperties extends V2CommonProperties {
  readonly surface: AnalyticsSurface;
  readonly action_id: string;
  readonly selected_count: number;
  readonly list_type: 'people';
  readonly list_id?: string;
  readonly create_new?: boolean;
  readonly reason?: string;
}
export interface DetailEventProperties extends V2CommonProperties {
  readonly surface: AnalyticsSurface;
  readonly detail_view_id: string;
  readonly resource: EntityReference;
  readonly result_position?: number;
  readonly load_source?: 'network' | 'cache';
  readonly reason?: string;
  readonly duration_ms?: number;
}
export interface FrontendEventPropertiesV2 {
  page_viewed: V2CommonProperties & { page_view_id: string; page: string; entry_source?: string };
  page_engagement: V2CommonProperties & { page_view_id: string; active_ms: number; duration_ms?: number };
  workflow_started: V2CommonProperties & WorkflowContext;
  company_selection_mode_changed: V2CommonProperties & { surface: AnalyticsSurface; enabled: boolean };
  company_selection_changed: SelectionEventProperties;
  people_selection_changed: SelectionEventProperties;
  related_people_requested: V2CommonProperties & { surface: AnalyticsSurface; action_id: string; selection_id: string; selected_count: number };
  map_view_requested: V2CommonProperties & { surface: AnalyticsSurface; action_id: string; selection_id: string; selected_count: number };
  company_view_requested: V2CommonProperties & { surface: AnalyticsSurface; action_id: string; resource: EntityReference };
  search_submitted: V2CommonProperties & { surface: AnalyticsSurface; search_id: string; trigger: SearchTrigger; submitted_criteria: CriteriaSnapshot; search_type?: string };
  filter_changed: V2CommonProperties & { surface: AnalyticsSurface; filter_key: string; change_type: 'add' | 'remove' | 'clear' | 'replace' | 'toggle' | 'range'; previous_value: CriterionValue; current_value: CriterionValue; entities?: readonly EntityReference[]; provenance: CriteriaProvenance };
  filters_applied: V2CommonProperties & { surface: AnalyticsSurface; submitted_criteria: CriteriaSnapshot; criteria_version: string; action_id: string; apply_reason?: 'apply' | 'reset' };
  filter_apply_blocked: V2CommonProperties & { surface: AnalyticsSurface; reason: string; filter_keys?: readonly string[] };
  pagination_changed: V2CommonProperties & { surface: AnalyticsSurface; search_id: string; action_id: string; previous_page: number | null; page_number: number | null; page_size: number; navigation_method: 'next' | 'previous' | 'jump' | 'page_size' | 'keyboard' };
  results_page_viewed: V2CommonProperties & { surface: AnalyticsSurface; search_id: string; view_id: string; displayed_count: number; total_count: number | null; count_status: CountStatus; load_source: 'network' | 'cache'; display_status?: 'partial' | 'complete' | 'failed'; aggregate?: boolean; location_count?: number; company_count?: number; navigation_method?: string };
  detail_opened: DetailEventProperties;
  detail_content_ready: DetailEventProperties;
  detail_content_failed: DetailEventProperties & { reason: string };
  company_tab_viewed: V2CommonProperties & { surface: AnalyticsSurface; resource: EntityReference; tab: 'products' | 'people' | 'locations'; previous_tab?: 'products' | 'people' | 'locations' | null; tab_view_id: string; view_reason: 'default' | 'user' | 'programmatic' };
  portfolio_match_inspected: V2CommonProperties & { surface: AnalyticsSurface; resource: EntityReference; parent_entity: EntityReference; detail_view_id: string; match_dimensions: readonly CriteriaDimension[]; match_status: 'matched' | 'not_matched' | 'unknown' };
  contact_unlock_requested: ContactEventProperties;
  contact_unlock_blocked: ContactEventProperties & { reason: string };
  contact_reveal_displayed: ContactEventProperties;
  people_list_add_requested: ListIntentProperties;
  people_list_add_blocked: ListIntentProperties & { reason: string };
  map_location_opened: V2CommonProperties & { surface: AnalyticsSurface; action_id: string; resource: EntityReference };
  map_cluster_expanded: V2CommonProperties & { surface: AnalyticsSurface; action_id: string; visible_count: number; cluster_id?: string };
  suggestion_selected: V2CommonProperties & { surface: AnalyticsSurface; resource: EntityReference; filter_key?: string };
  ui_interaction: V2CommonProperties & { surface: AnalyticsSurface; action: string; control_type?: string; resource?: EntityReference };
  download_created: V2CommonProperties & { operation: string; resource?: EntityReference; item_count?: number };
  network_failed: V2CommonProperties & { operation: string; operation_id: string; reason?: string };
  agent_prompt_submitted: V2CommonProperties & { operation_id: string; conversation_id?: string; prompt_length?: number };
  application_error: V2CommonProperties & { reason: string; operation?: string };
}
export interface BackendEventPropertiesV2 {
  signed_in: V2CommonProperties & { method?: string };
  registered: V2CommonProperties & { method?: string };
  signed_out: V2CommonProperties & { operation?: string };
  search_completed: V2CommonProperties & SearchOutcome & { search_id: string };
  search_page_returned: V2CommonProperties & SearchOutcome & { search_id: string };
  search_failed: V2CommonProperties & SearchOutcome & { search_id: string };
  search_count_resolved: V2CommonProperties & CountOutcome & { search_id: string };
  operation_completed: V2CommonProperties & (MutationOutcome | ReadOutcome);
  operation_failed: V2CommonProperties & (MutationOutcome | ReadOutcome);
  unlock_completed: V2CommonProperties & UnlockOutcome;
  unlock_failed: V2CommonProperties & UnlockOutcome;
  credits_consumed: V2CommonProperties & CreditOutcome;
  action_blocked: V2CommonProperties & AccessOutcome;
  agent_completed: V2CommonProperties & { operation: string; conversation_id?: string; duration_ms?: number; input_tokens?: number; output_tokens?: number };
  agent_failed: V2CommonProperties & { operation: string; conversation_id?: string; duration_ms?: number; error_code?: string };
  agent_cancelled: V2CommonProperties & { operation: string; conversation_id?: string; duration_ms?: number };
}
export type EventPropertiesV2 = FrontendEventPropertiesV2 & BackendEventPropertiesV2;

export type ValidationCode = 'invalid_type' | 'missing_required' | 'unknown_field' |
  'invalid_value' | 'too_large' | 'invalid_event' | 'wrong_source' |
  'unsupported_version' | 'invalid_encoding';
export interface ValidationIssue { readonly path: string; readonly code: ValidationCode; }
export type ValidationResult<T> =
  | { readonly valid: true; readonly value: T }
  | { readonly valid: false; readonly errors: readonly ValidationIssue[] };

type Problems = ValidationIssue[];
type Rule = (input: unknown, path: string, problems: Problems) => unknown;
type Shape = Readonly<Record<string, Rule>>;
const issue = (problems: Problems, path: string, code: ValidationCode): void => {
  if (problems.length < 20) problems.push({ path, code });
};
const isPlain = (input: unknown): input is Record<string, unknown> => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return false;
  const prototype = Object.getPrototypeOf(input);
  return prototype === Object.prototype || prototype === null;
};
const stringRule = (max = 256, pattern?: RegExp): Rule => (input, path, problems) => {
  if (typeof input !== 'string') { issue(problems, path, 'invalid_type'); return undefined; }
  if (!input.length || input.length > max) {
    issue(problems, path, input.length > max ? 'too_large' : 'invalid_value');
    return undefined;
  }
  if (/\p{Cc}/u.test(input) || (pattern && !pattern.test(input))) {
    issue(problems, path, 'invalid_value'); return undefined;
  }
  return input;
};
const enumRule = (values: readonly (string | number)[]): Rule => (input, path, problems) => {
  if (!values.includes(input as string | number)) {
    issue(problems, path, 'invalid_value'); return undefined;
  }
  return input;
};
const numberRule = (minimum = 0, integer = false): Rule => (input, path, problems) => {
  if (typeof input !== 'number' || !Number.isFinite(input) || input < minimum ||
      (integer && !Number.isSafeInteger(input))) {
    issue(problems, path, 'invalid_value'); return undefined;
  }
  return input;
};
const booleanRule: Rule = (input, path, problems) => {
  if (typeof input !== 'boolean') { issue(problems, path, 'invalid_type'); return undefined; }
  return input;
};
const nullable = (rule: Rule): Rule => (input, path, problems) =>
  input === null ? null : rule(input, path, problems);
const arrayRule = (rule: Rule, max = ANALYTICS_MAX_ARRAY_ITEMS): Rule => (input, path, problems) => {
  if (!Array.isArray(input)) { issue(problems, path, 'invalid_type'); return undefined; }
  if (input.length > max) issue(problems, path, 'too_large');
  return input.slice(0, max).map((value, index) => rule(value, `${path}[${index}]`, problems));
};
const objectRule = (
  shape: Shape,
  required: readonly string[] = [],
  refine?: (value: Record<string, unknown>, path: string, problems: Problems) => void,
): Rule => (input, path, problems) => {
  if (!isPlain(input)) { issue(problems, path, 'invalid_type'); return undefined; }
  const result: Record<string, unknown> = {};
  const keys = Object.keys(input);
  if (keys.length > 100) issue(problems, path, 'too_large');
  for (const key of keys.slice(0, 101)) {
    if (input[key] === undefined) continue;
    if (!Object.prototype.hasOwnProperty.call(shape, key)) {
      // Unknown keys may themselves contain private text. Never include them in diagnostics.
      issue(problems, `${path}.$unknown`, 'unknown_field');
    }
  }
  // Shape order, including nested references, makes serialization independent of
  // caller insertion order. Inherited values never become attribution or facts.
  for (const key of Object.keys(shape)) {
    if (!Object.prototype.hasOwnProperty.call(input, key) || input[key] === undefined) continue;
    const value = shape[key](input[key], `${path}.${key}`, problems);
    if (value !== undefined) result[key] = value;
  }
  for (const key of required) {
    if (!Object.prototype.hasOwnProperty.call(input, key) || input[key] === undefined) issue(problems, `${path}.${key}`, 'missing_required');
  }
  refine?.(result, path, problems);
  return result;
};
const idRule = stringRule(128, /^[a-zA-Z0-9][a-zA-Z0-9_.:-]{4,127}$/);
const entityIdRule = stringRule(128, /^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/);
const codeRule = stringRule(128, /^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/);
const countRule = numberRule(0, true);
const nullableCountRule = nullable(countRule);
const surfaceRule = enumRule(ANALYTICS_SURFACES);
const companyTypeRule = enumRule(['marketer', 'manufacturer']);
const countStatusRule = enumRule(['pending', 'ready', 'unavailable']);
const labelStatusRule = enumRule(['resolved', 'partial', 'unresolved']);
const entityShape: Shape = {
  id: entityIdRule, namespace: enumRule(ENTITY_NAMESPACES), name: stringRule(),
  linked_ids: arrayRule(objectRule({ id: entityIdRule, namespace: enumRule(ENTITY_NAMESPACES) }, ['id', 'namespace']), 4),
  label_source: enumRule(['loaded_entity', 'dictionary', 'browser_option']),
  label_resolution_status: enumRule(['resolved', 'unresolved']), company_type: companyTypeRule,
};
const entityRule = objectRule(entityShape, ['id', 'namespace', 'label_resolution_status'], (value, path, problems) => {
  if (value.label_resolution_status === 'resolved' && (!value.name || !value.label_source))
    issue(problems, `${path}.label_resolution_status`, 'invalid_value');
  if (value.label_resolution_status === 'unresolved' && (value.name || value.label_source))
    issue(problems, `${path}.label_resolution_status`, 'invalid_value');
  if (Array.isArray(value.linked_ids)) {
    const keys = value.linked_ids.filter(isPlain).map((identity) => `${identity.namespace}:${identity.id}`);
    if (new Set(keys).size !== keys.length || keys.includes(`${value.namespace}:${value.id}`))
      issue(problems, `${path}.linked_ids`, 'invalid_value');
  }
});
const selectionRule = objectRule({
  selection_id: idRule, selection_version: numberRule(1, true), selected_count: countRule,
  included_count: countRule, truncated: booleanRule, entities: arrayRule(entityRule),
}, ['selection_id', 'selection_version', 'selected_count', 'included_count', 'truncated', 'entities'], (value, path, problems) => {
  if (Array.isArray(value.entities) && value.included_count !== value.entities.length)
    issue(problems, `${path}.included_count`, 'invalid_value');
  if (typeof value.included_count === 'number' && typeof value.selected_count === 'number' &&
      (value.included_count > value.selected_count || value.truncated !== (value.included_count < value.selected_count)))
    issue(problems, `${path}.truncated`, 'invalid_value');
  if (Array.isArray(value.entities)) {
    const keys = value.entities.filter(isPlain).map((entity) => `${entity.namespace}:${entity.id}`);
    if (new Set(keys).size !== keys.length) issue(problems, `${path}.entities`, 'invalid_value');
  }
});
const criterionScalar: Rule = (input, path, problems) => {
  if (input === null || typeof input === 'boolean') return input;
  if (typeof input === 'number' && Number.isFinite(input)) return input;
  if (typeof input === 'string' && input.length <= 2048 && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(input)) return input;
  issue(problems, path, typeof input === 'string' && input.length > 2048 ? 'too_large' : 'invalid_type');
  return undefined;
};
const criterionValueRule: Rule = (input, path, problems) => {
  if (Array.isArray(input)) {
    const result = arrayRule(criterionScalar)(input, path, problems);
    if (input.some((item) => item === null || typeof item !== typeof input[0]))
      issue(problems, path, 'invalid_value');
    return result;
  }
  if (isPlain(input)) {
    return objectRule({ min: criterionScalar, max: criterionScalar }, [], (value, rangePath, rangeProblems) => {
      if (value.min === undefined && value.max === undefined) issue(rangeProblems, rangePath, 'invalid_value');
      for (const key of ['min', 'max']) {
        if (value[key] !== undefined && !['number', 'string'].includes(typeof value[key]))
          issue(rangeProblems, `${rangePath}.${key}`, 'invalid_value');
      }
      if (value.min !== undefined && value.max !== undefined && typeof value.min !== typeof value.max)
        issue(rangeProblems, rangePath, 'invalid_value');
      if (typeof value.min === 'number' && typeof value.max === 'number' && value.min > value.max)
        issue(rangeProblems, rangePath, 'invalid_value');
    })(input, path, problems);
  }
  return criterionScalar(input, path, problems);
};
const provenanceRule = enumRule(['user', 'portfolio', 'inherited_company_selection', 'system_scope']);
const dimensionRule = objectRule({
  key: codeRule, value: criterionValueRule, active: booleanRule, provenance: provenanceRule,
  operator: enumRule(['eq', 'neq', 'in', 'not_in', 'contains', 'range', 'gte', 'lte']),
  unit: stringRule(32), entities: arrayRule(entityRule),
}, ['key', 'value', 'active', 'provenance']);
const criteriaRule = objectRule({
  criteria_version: codeRule, query: criterionScalar, dimensions: arrayRule(dimensionRule),
  filter_keys: arrayRule(codeRule), filter_count: countRule, filter_combination: criterionScalar,
  label_resolution_status: labelStatusRule, unresolved_count: countRule,
  included_count: countRule, truncated: booleanRule,
}, ['criteria_version', 'dimensions', 'filter_keys', 'filter_count'], (value, path, problems) => {
  if (value.query !== undefined && typeof value.query !== 'string') issue(problems, `${path}.query`, 'invalid_type');
  if (value.filter_combination !== undefined && typeof value.filter_combination !== 'string') issue(problems, `${path}.filter_combination`, 'invalid_type');
  if (Array.isArray(value.filter_keys)) {
    if (value.filter_count !== value.filter_keys.length || new Set(value.filter_keys).size !== value.filter_keys.length)
      issue(problems, `${path}.filter_count`, 'invalid_value');
    if (Array.isArray(value.dimensions) && value.truncated !== true) {
      const filterKeys = value.filter_keys;
      const active = value.dimensions.filter(isPlain).filter((dimension) => dimension.active).map((dimension) => dimension.key);
      if (new Set(active).size !== active.length || active.length !== filterKeys.length ||
          active.some((key) => !filterKeys.includes(key))) issue(problems, `${path}.filter_keys`, 'invalid_value');
    }
  }
});
const attributionShape: Shape = {
  workflow_id: idRule, workflow_type: enumRule(WORKFLOW_TYPES), parent_workflow_id: idRule,
  selection_id: idRule, selection_version: numberRule(1, true), action_id: idRule, attempt_id: idRule,
  origin_page_view_id: idRule, origin_search_id: idRule, parent_search_id: idRule, page_view_id: idRule,
  surface: surfaceRule, source_surface: surfaceRule,
  source_company_type: enumRule(['marketer', 'manufacturer', 'mixed']),
  destination: enumRule(['people', 'map', 'company']), parent_entity: entityRule,
  trigger: enumRule(SEARCH_TRIGGERS), execution_kind: enumRule(['interactive', 'automatic', 'deferred']),
  page_number: nullable(numberRule(1, true)), page_size: numberRule(1, true),
  previous_page: nullable(numberRule(1, true)), offset: countRule, cursor_present: booleanRule,
  selected_count: countRule, included_count: countRule, truncated: booleanRule,
};
export const ATTRIBUTION_FIELDS = Object.freeze(Object.keys(attributionShape)) as readonly (keyof AttributionContext)[];
const refineAttribution = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  if (!!value.workflow_id !== !!value.workflow_type) issue(problems, `${path}.workflow_id`, 'invalid_value');
  if (typeof value.included_count === 'number' && typeof value.selected_count === 'number' && value.included_count > value.selected_count)
    issue(problems, `${path}.included_count`, 'invalid_value');
};
const attributionRule = objectRule(attributionShape, [], refineAttribution);

const pageShape: Shape = {
  page_number: nullable(numberRule(1, true)), page_size: numberRule(1, true),
  offset: countRule, cursor_present: booleanRule,
};
const searchOutcomes = ['success', 'no_results', 'upstream_unavailable', 'validation_failed', 'access_denied', 'failed'] as const;
const searchShape: Shape = {
  ...pageShape, operation: codeRule, outcome: enumRule(searchOutcomes), returned_count: nullableCountRule,
  total_count: nullableCountRule, count_status: countStatusRule, zero_results: booleanRule, submitted_criteria: criteriaRule,
  effective_criteria: criteriaRule, parent_entity: entityRule, duration_ms: numberRule(), error_code: codeRule,
  provider: codeRule, total_available_count: nullableCountRule, portfolio_matched_count: nullableCountRule,
  has_match_criteria: booleanRule, matched_only: booleanRule, portfolio_scope: codeRule,
  location_count: nullableCountRule, company_count: nullableCountRule,
  label_resolution_status: labelStatusRule, unresolved_count: countRule,
};
const countShape: Shape = {
  operation: codeRule, total_count: nullableCountRule, count_status: countStatusRule, zero_results: booleanRule,
  resolution_id: idRule, duration_ms: numberRule(), error_code: codeRule,
  total_available_count: nullableCountRule, portfolio_matched_count: nullableCountRule,
};
const mutationShape: Shape = {
  operation: codeRule, outcome: enumRule(['success', 'no_change', 'partial', 'failed']),
  resource: entityRule, list_id: entityIdRule, list_type: enumRule(['people', 'products']),
  requested_count: countRule, added_count: countRule, already_present_count: countRule,
  removed_count: countRule, failed_count: countRule, affected_count: countRule,
  created_count: countRule, updated_count: countRule, linked_count: countRule,
  provider: codeRule, duration_ms: numberRule(), error_code: codeRule,
};
const unlockShape: Shape = {
  operation: codeRule, outcome: enumRule(['found', 'unavailable', 'provider_failure', 'invalid_resource', 'retry_restricted', 'insufficient_credits', 'access_denied', 'failed']),
  contact_type: enumRule(['email', 'phone']), resource: entityRule, attempt_id: idRule,
  credits_charged: numberRule(), already_unlocked: booleanRule, shared_unlock: booleanRule,
  purpose: codeRule, reason: codeRule, duration_ms: numberRule(),
};
const creditShape: Shape = {
  operation: codeRule, amount: numberRule(Number.MIN_VALUE), consumption_id: numberRule(1, true),
  purpose: codeRule, resource: entityRule, unit: enumRule(['credits']), attempt_id: idRule,
  contact_type: enumRule(['email', 'phone']),
};
const accessShape: Shape = { operation: codeRule, reason: codeRule, feature: codeRule, status_code: numberRule(100, true), resource: entityRule };
const readShape: Shape = { operation: codeRule, outcome: enumRule(searchOutcomes), resource: entityRule, returned_count: nullableCountRule, duration_ms: numberRule(), error_code: codeRule };
const refineRead = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  if (['success', 'no_results'].includes(String(value.outcome)) && value.resource === undefined)
    issue(problems, `${path}.resource`, 'missing_required');
};
const refineCount = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  if (value.count_status === 'ready' ? typeof value.total_count !== 'number' : value.total_count !== null)
    issue(problems, `${path}.total_count`, 'invalid_value');
  if (typeof value.total_available_count === 'number' && typeof value.portfolio_matched_count === 'number' && value.portfolio_matched_count > value.total_available_count)
    issue(problems, `${path}.portfolio_matched_count`, 'invalid_value');
  if (value.zero_results !== undefined && (value.count_status !== 'ready' || value.zero_results !== (value.total_count === 0)))
    issue(problems, `${path}.zero_results`, 'invalid_value');
};
const refineSearch = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  refineCount(value, path, problems);
  if (value.outcome === 'no_results' && (value.total_count !== 0 || value.returned_count !== 0 || value.count_status !== 'ready'))
    issue(problems, `${path}.outcome`, 'invalid_value');
  if (['upstream_unavailable', 'validation_failed', 'access_denied', 'failed'].includes(String(value.outcome)) &&
      (value.total_count !== null || value.returned_count !== null || value.count_status !== 'unavailable'))
    issue(problems, `${path}.outcome`, 'invalid_value');
};
const refineMutation = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  if (value.outcome === 'no_change' && ['added_count', 'removed_count', 'affected_count', 'created_count', 'updated_count', 'linked_count'].some((key) => typeof value[key] === 'number' && (value[key] as number) > 0))
    issue(problems, `${path}.outcome`, 'invalid_value');
  if (typeof value.requested_count === 'number') {
    for (const key of ['added_count', 'already_present_count', 'removed_count', 'failed_count'])
      if (typeof value[key] === 'number' && (value[key] as number) > value.requested_count)
        issue(problems, `${path}.${key}`, 'invalid_value');
    if (['added_count', 'already_present_count', 'failed_count'].every((key) => typeof value[key] === 'number') &&
        (value.added_count as number) + (value.already_present_count as number) + (value.failed_count as number) !== value.requested_count)
      issue(problems, `${path}.requested_count`, 'invalid_value');
  }
};
const refineUnlock = (value: Record<string, unknown>, path: string, problems: Problems): void => {
  if (value.outcome !== 'invalid_resource' && value.resource === undefined)
    issue(problems, `${path}.resource`, 'missing_required');
  if ((value.outcome !== 'found' || value.already_unlocked === true || value.shared_unlock === true) && value.credits_charged !== 0)
    issue(problems, `${path}.credits_charged`, 'invalid_value');
};
const domainRules: { [K in DomainFact]: Rule } = {
  search: objectRule(searchShape, ['operation', 'outcome', 'returned_count', 'total_count', 'count_status'], refineSearch),
  count: objectRule(countShape, ['operation', 'total_count', 'count_status'], refineCount),
  mutation: objectRule(mutationShape, ['operation', 'outcome'], refineMutation),
  unlock: objectRule(unlockShape, ['operation', 'outcome', 'contact_type', 'attempt_id', 'credits_charged'], refineUnlock),
  credit: objectRule(creditShape, ['operation', 'amount', 'consumption_id', 'purpose', 'resource']),
  access: objectRule(accessShape, ['operation', 'reason']),
  read: objectRule(readShape, ['operation', 'outcome'], refineRead),
};

const commonShape: Shape = {
  ...attributionShape, schema_version: enumRule([2]), source: enumRule(['frontend', 'backend']),
  environment: codeRule, release_version: codeRule, client_id: nullable(numberRule(1, true)),
  client_name: stringRule(), client_slug: codeRule,
  tenant_resolution: enumRule(['resolved', 'none', 'unavailable']), application_scope: codeRule,
  search_id: idRule, operation_id: idRule, search_phase: enumRule(['submit', 'page', 'count']),
  page: stringRule(128, /^\/[a-zA-Z0-9_/:.-]*$/), user_type: codeRule, role: codeRule,
  is_internal: booleanRule, properties_truncated: booleanRule, status_code: numberRule(100, true),
};
interface EventRuleDefinition {
  readonly shape: Shape;
  readonly required: readonly string[];
  readonly refine?: (value: Record<string, unknown>, path: string, problems: Problems) => void;
}
const definition = (shape: Shape, required: readonly string[] = [], refine?: EventRuleDefinition['refine']): EventRuleDefinition => ({ shape, required, refine });
const selectionEventShape: Shape = {
  selection: selectionRule, change_type: enumRule(['select', 'deselect', 'clear', 'replace']),
  selected_after: booleanRule, resource: entityRule,
};
const contactEventShape: Shape = {
  contact_type: enumRule(['email', 'phone']), resource: entityRule, reason: codeRule,
  availability: enumRule(['new', 'already_visible', 'shared', 'unavailable']),
};
const listEventShape: Shape = {
  list_type: enumRule(['people']), list_id: entityIdRule, create_new: booleanRule, reason: codeRule,
};
const detailEventShape: Shape = {
  detail_view_id: idRule, resource: entityRule, result_position: numberRule(1, true),
  load_source: enumRule(['network', 'cache']), reason: codeRule, duration_ms: numberRule(),
};
const detailRequired = ['surface', 'detail_view_id', 'resource'];
const contactRequired = ['surface', 'attempt_id', 'contact_type', 'resource'];
const listRequired = ['surface', 'action_id', 'selected_count', 'list_type'];
const searchRequired = ['operation', 'outcome', 'returned_count', 'total_count', 'count_status', 'search_id'];
const unlockRequired = ['operation', 'outcome', 'contact_type', 'attempt_id', 'credits_charged'];
const agentShape = { operation: codeRule, conversation_id: entityIdRule, duration_ms: numberRule(), input_tokens: countRule, output_tokens: countRule, error_code: codeRule };
const operationOutcomeShape = { ...mutationShape, ...readShape, outcome: enumRule([...searchOutcomes, 'no_change', 'partial']) };
const eventDefinitions: { [K in AnalyticsEventV2]: EventRuleDefinition } = {
  page_viewed: definition({ entry_source: codeRule }, ['page_view_id', 'page']),
  page_engagement: definition({ active_ms: numberRule(), duration_ms: numberRule() }, ['page_view_id', 'active_ms']),
  workflow_started: definition({}, ['workflow_id', 'workflow_type', 'surface', 'origin_page_view_id']),
  company_selection_mode_changed: definition({ enabled: booleanRule }, ['surface', 'enabled']),
  company_selection_changed: definition(selectionEventShape, ['surface', 'selection', 'change_type']),
  people_selection_changed: definition(selectionEventShape, ['surface', 'selection', 'change_type']),
  related_people_requested: definition({}, ['surface', 'action_id', 'selection_id', 'selected_count']),
  map_view_requested: definition({}, ['surface', 'action_id', 'selection_id', 'selected_count']),
  company_view_requested: definition({ resource: entityRule }, ['surface', 'action_id', 'resource']),
  search_submitted: definition({ submitted_criteria: criteriaRule, search_type: codeRule }, ['surface', 'search_id', 'trigger', 'submitted_criteria']),
  filter_changed: definition({
    filter_key: codeRule, change_type: enumRule(['add', 'remove', 'clear', 'replace', 'toggle', 'range']),
    previous_value: criterionValueRule, current_value: criterionValueRule,
    entities: arrayRule(entityRule), provenance: provenanceRule,
  }, ['surface', 'filter_key', 'change_type', 'previous_value', 'current_value', 'provenance']),
  filters_applied: definition({ submitted_criteria: criteriaRule, criteria_version: codeRule, apply_reason: enumRule(['apply', 'reset']) }, ['surface', 'submitted_criteria', 'criteria_version', 'action_id']),
  filter_apply_blocked: definition({ reason: codeRule, filter_keys: arrayRule(codeRule) }, ['surface', 'reason']),
  pagination_changed: definition({ navigation_method: enumRule(['next', 'previous', 'jump', 'page_size', 'keyboard']) }, ['surface', 'search_id', 'action_id', 'previous_page', 'page_number', 'page_size', 'navigation_method']),
  results_page_viewed: definition({
    view_id: idRule, displayed_count: countRule, total_count: nullableCountRule, count_status: countStatusRule,
    load_source: enumRule(['network', 'cache']), display_status: enumRule(['partial', 'complete', 'failed']),
    aggregate: booleanRule, location_count: countRule, company_count: countRule, navigation_method: codeRule,
  }, ['surface', 'search_id', 'view_id', 'displayed_count', 'total_count', 'count_status', 'load_source'], refineCount),
  detail_opened: definition(detailEventShape, detailRequired),
  detail_content_ready: definition(detailEventShape, detailRequired),
  detail_content_failed: definition(detailEventShape, [...detailRequired, 'reason']),
  company_tab_viewed: definition({
    resource: entityRule, tab: enumRule(['products', 'people', 'locations']),
    previous_tab: nullable(enumRule(['products', 'people', 'locations'])), tab_view_id: idRule,
    view_reason: enumRule(['default', 'user', 'programmatic']),
  }, ['surface', 'resource', 'tab', 'tab_view_id', 'view_reason']),
  portfolio_match_inspected: definition({
    resource: entityRule, detail_view_id: idRule, match_dimensions: arrayRule(dimensionRule),
    match_status: enumRule(['matched', 'not_matched', 'unknown']),
  }, ['surface', 'resource', 'parent_entity', 'detail_view_id', 'match_dimensions', 'match_status']),
  contact_unlock_requested: definition(contactEventShape, contactRequired),
  contact_unlock_blocked: definition(contactEventShape, [...contactRequired, 'reason']),
  contact_reveal_displayed: definition(contactEventShape, contactRequired),
  people_list_add_requested: definition(listEventShape, listRequired),
  people_list_add_blocked: definition(listEventShape, [...listRequired, 'reason']),
  map_location_opened: definition({ resource: entityRule }, ['surface', 'action_id', 'resource']),
  map_cluster_expanded: definition({ visible_count: countRule, cluster_id: idRule }, ['surface', 'action_id', 'visible_count']),
  suggestion_selected: definition({ resource: entityRule, filter_key: codeRule }, ['surface', 'resource']),
  ui_interaction: definition({ action: codeRule, control_type: codeRule, resource: entityRule }, ['surface', 'action']),
  download_created: definition({ operation: codeRule, resource: entityRule, item_count: countRule }, ['operation']),
  network_failed: definition({ operation: codeRule, reason: codeRule }, ['operation', 'operation_id']),
  agent_prompt_submitted: definition({ conversation_id: entityIdRule, prompt_length: countRule }, ['operation_id']),
  application_error: definition({ reason: codeRule, operation: codeRule }, ['reason']),
  signed_in: definition({ method: codeRule }), registered: definition({ method: codeRule }),
  signed_out: definition({ operation: codeRule }),
  search_completed: definition(searchShape, searchRequired, refineSearch),
  search_page_returned: definition(searchShape, searchRequired, refineSearch),
  search_failed: definition(searchShape, searchRequired, refineSearch),
  search_count_resolved: definition(countShape, ['operation', 'total_count', 'count_status', 'search_id'], refineCount),
  operation_completed: definition(operationOutcomeShape, ['operation', 'outcome'], refineMutation),
  operation_failed: definition(operationOutcomeShape, ['operation', 'outcome'], refineMutation),
  unlock_completed: definition(unlockShape, unlockRequired, refineUnlock),
  unlock_failed: definition(unlockShape, unlockRequired, refineUnlock),
  credits_consumed: definition(creditShape, ['operation', 'amount', 'consumption_id', 'purpose', 'resource']),
  action_blocked: definition(accessShape, ['operation', 'reason']),
  agent_completed: definition(agentShape, ['operation']),
  agent_failed: definition(agentShape, ['operation']),
  agent_cancelled: definition(agentShape, ['operation']),
};

// Activity recommendations concern analysis, not a replacement for the browser's
// existing inactivity timer. Automatic/deferred facts never renew that timer.
export const V2_EVENT_DEFINITIONS = Object.freeze(Object.fromEntries(
  [...V2_FRONTEND_EVENTS, ...V2_BACKEND_EVENTS].map((event) => [event, Object.freeze({
    source: (V2_FRONTEND_EVENTS as readonly string[]).includes(event) ? 'frontend' : 'backend',
    required: Object.freeze([...eventDefinitions[event].required]),
    activity: ['page_engagement', 'search_count_resolved', 'network_failed', 'application_error', 'detail_content_ready', 'detail_content_failed'].includes(event)
      ? 'inactive' : ['search_submitted', 'search_completed', 'search_page_returned', 'results_page_viewed'].includes(event)
      ? 'trigger_dependent' : (V2_BACKEND_EVENTS as readonly string[]).includes(event) ? 'inactive' : 'active',
  })]),
)) as Readonly<Record<AnalyticsEventV2, {
  readonly source: AnalyticsSource;
  readonly required: readonly string[];
  readonly activity: 'active' | 'inactive' | 'trigger_dependent';
}>>;

function utf8Bytes(value: string): number[] {
  const result: number[] = [];
  for (let index = 0; index < value.length; index++) {
    const first = value.charCodeAt(index);
    let point = first;
    if (first >= 0xd800 && first <= 0xdbff) {
      const second = value.charCodeAt(++index);
      if (!(second >= 0xdc00 && second <= 0xdfff)) throw new Error('invalid_utf8');
      point = 0x10000 + ((first - 0xd800) << 10) + second - 0xdc00;
    } else if (first >= 0xdc00 && first <= 0xdfff) throw new Error('invalid_utf8');
    if (point < 0x80) result.push(point);
    else if (point < 0x800) result.push(0xc0 | (point >> 6), 0x80 | (point & 63));
    else if (point < 0x10000) result.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 63), 0x80 | (point & 63));
    else result.push(0xf0 | (point >> 18), 0x80 | ((point >> 12) & 63), 0x80 | ((point >> 6) & 63), 0x80 | (point & 63));
  }
  return result;
}
function validateWith<T>(rule: Rule, input: unknown): ValidationResult<T> {
  const problems: Problems = [];
  try {
    const value = rule(input, '$', problems);
    if (!problems.length && utf8Bytes(JSON.stringify(value)).length > ANALYTICS_MAX_PROPERTY_BYTES)
      issue(problems, '$', 'too_large');
    return problems.length ? { valid: false, errors: problems } : { valid: true, value: value as T };
  } catch {
    return { valid: false, errors: [{ path: '$', code: 'invalid_type' }] };
  }
}
export function validateAttributionContext(input: unknown): ValidationResult<AttributionContext> {
  return validateWith(attributionRule, input);
}
export function projectAttributionContext(input: unknown): ValidationResult<AttributionContext> {
  try {
    if (!isPlain(input)) return { valid: false, errors: [{ path: '$', code: 'invalid_type' }] };
    const projected: Record<string, unknown> = {};
    for (const key of ATTRIBUTION_FIELDS)
      if (Object.prototype.hasOwnProperty.call(input, key) && input[key] !== undefined) projected[key] = input[key];
    return validateAttributionContext(projected);
  } catch { return { valid: false, errors: [{ path: '$', code: 'invalid_type' }] }; }
}
export function validateEntityReference(input: unknown): ValidationResult<EntityReference> {
  return validateWith(entityRule, input);
}
export function validateSelectionSnapshot(input: unknown): ValidationResult<SelectionSnapshot> {
  return validateWith(selectionRule, input);
}
export function validateCriteriaSnapshot(input: unknown): ValidationResult<CriteriaSnapshot> {
  return validateWith(criteriaRule, input);
}
export function validateDomainOutcome<K extends DomainFact>(fact: K, input: unknown): ValidationResult<DomainOutcomeByFact[K]> {
  if (!Object.prototype.hasOwnProperty.call(domainRules, fact))
    return { valid: false, errors: [{ path: '$', code: 'invalid_value' }] };
  return validateWith(domainRules[fact], input);
}
export function validateV2Event<E extends AnalyticsEventV2>(
  event: E,
  properties: unknown,
  source?: AnalyticsSource,
): ValidationResult<EventPropertiesV2[E]> {
  if (!Object.prototype.hasOwnProperty.call(eventDefinitions, event))
    return { valid: false, errors: [{ path: '$.event', code: 'invalid_event' }] };
  const owner = V2_EVENT_DEFINITIONS[event].source;
  if (source !== undefined && source !== owner)
    return { valid: false, errors: [{ path: '$.source', code: 'wrong_source' }] };
  const spec = eventDefinitions[event];
  return validateWith(objectRule({ ...commonShape, ...spec.shape }, spec.required, (value, path, problems) => {
    refineAttribution(value, path, problems);
    spec.refine?.(value, path, problems);
    if (value.source !== undefined && value.source !== owner) issue(problems, `${path}.source`, 'wrong_source');
    if (value.schema_version !== undefined && value.schema_version !== 2) issue(problems, `${path}.schema_version`, 'unsupported_version');
    if (typeof value.status_code === 'number' && value.status_code > 599) issue(problems, `${path}.status_code`, 'invalid_value');
    const successfulSearch = value.outcome === 'success' || value.outcome === 'no_results';
    if ((event === 'search_failed' && successfulSearch) ||
        (['search_completed', 'search_page_returned'].includes(event) && !successfulSearch))
      issue(problems, `${path}.outcome`, 'invalid_value');
    if (event === 'search_count_resolved' && value.count_status === 'pending') issue(problems, `${path}.count_status`, 'invalid_value');
    if ((event === 'unlock_completed' && value.outcome !== 'found') || (event === 'unlock_failed' && value.outcome === 'found'))
      issue(problems, `${path}.outcome`, 'invalid_value');
    if (event === 'operation_completed' && ['failed', 'upstream_unavailable', 'validation_failed', 'access_denied'].includes(String(value.outcome)))
      issue(problems, `${path}.outcome`, 'invalid_value');
    if (event === 'operation_failed' && !['failed', 'upstream_unavailable', 'validation_failed', 'access_denied'].includes(String(value.outcome)))
      issue(problems, `${path}.outcome`, 'invalid_value');
    if (event === 'contact_reveal_displayed' && value.availability === 'unavailable')
      issue(problems, `${path}.availability`, 'invalid_value');
  }), properties);
}
export function validateSemanticEvent<E extends FrontendEventV2>(event: E, properties: unknown): ValidationResult<FrontendEventPropertiesV2[E]> {
  return validateV2Event(event, properties, 'frontend');
}

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
function encodeBase64Url(bytes: readonly number[]): string {
  let value = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const bits = (bytes[index] << 16) | ((bytes[index + 1] || 0) << 8) | (bytes[index + 2] || 0);
    value += BASE64URL[(bits >>> 18) & 63] + BASE64URL[(bits >>> 12) & 63];
    if (index + 1 < bytes.length) value += BASE64URL[(bits >>> 6) & 63];
    if (index + 2 < bytes.length) value += BASE64URL[bits & 63];
  }
  return value;
}
function decodeBase64Url(value: string): number[] {
  if (!/^[A-Za-z0-9_-]+$/.test(value) || value.length % 4 === 1) throw new Error('invalid_encoding');
  const bytes: number[] = [];
  let bits = 0;
  let count = 0;
  for (const character of value) {
    bits = (bits << 6) | BASE64URL.indexOf(character);
    count += 6;
    if (count >= 8) { count -= 8; bytes.push((bits >>> count) & 255); }
  }
  if (count && (bits & ((1 << count) - 1))) throw new Error('invalid_encoding');
  return bytes;
}
function decodeUtf8(bytes: readonly number[]): string {
  let value = '';
  for (let index = 0; index < bytes.length;) {
    const first = bytes[index++];
    if (first < 0x80) { value += String.fromCharCode(first); continue; }
    const length = first >= 0xc2 && first <= 0xdf ? 2 : first >= 0xe0 && first <= 0xef ? 3 : first >= 0xf0 && first <= 0xf4 ? 4 : 0;
    if (!length || index + length - 1 > bytes.length) throw new Error('invalid_utf8');
    let point = first & (length === 2 ? 31 : length === 3 ? 15 : 7);
    for (let offset = 1; offset < length; offset++) {
      const byte = bytes[index++];
      if ((byte & 0xc0) !== 0x80) throw new Error('invalid_utf8');
      point = (point << 6) | (byte & 63);
    }
    if (point < (length === 2 ? 0x80 : length === 3 ? 0x800 : 0x10000) || point > 0x10ffff || (point >= 0xd800 && point <= 0xdfff))
      throw new Error('invalid_utf8');
    value += String.fromCodePoint(point);
  }
  return value;
}
export function encodeAttributionContext(context: AttributionContext): string | undefined {
  const checked = validateAttributionContext(context);
  if (!checked.valid) return undefined;
  try {
    // Fixed canonical key order makes equal contexts serialize identically.
    const wire: Record<string, unknown> = { v: ANALYTICS_V2_VERSION };
    for (const key of ATTRIBUTION_FIELDS) if (checked.value[key] !== undefined) wire[key] = checked.value[key];
    const encoded = encodeBase64Url(utf8Bytes(JSON.stringify(wire)));
    return encoded.length <= HEADER_MAX_ENCODED_BYTES ? encoded : undefined;
  } catch { return undefined; }
}
export function decodeAttributionContext(raw: unknown): ValidationResult<AttributionContext> {
  if (typeof raw !== 'string') return { valid: false, errors: [{ path: '$', code: 'invalid_type' }] };
  if (!raw.length || raw.length > HEADER_MAX_ENCODED_BYTES)
    return { valid: false, errors: [{ path: '$', code: raw.length ? 'too_large' : 'invalid_encoding' }] };
  try {
    const decoded = JSON.parse(decodeUtf8(decodeBase64Url(raw)));
    if (!isPlain(decoded)) return { valid: false, errors: [{ path: '$', code: 'invalid_type' }] };
    if (decoded.v !== ANALYTICS_V2_VERSION) return { valid: false, errors: [{ path: '$.v', code: 'unsupported_version' }] };
    const { v: _version, ...attribution } = decoded;
    return validateAttributionContext(attribution);
  } catch { return { valid: false, errors: [{ path: '$', code: 'invalid_encoding' }] }; }
}
